/**
 * Apparent and absolute magnitude, the distance modulus and brightness ratios.
 *
 * Magnitudes are logarithmic: five magnitudes is exactly a factor of 100 in brightness,
 * so one magnitude is 100^(1/5) = 10^0.4 ≈ 2.512, and a lower number is brighter. The
 * absolute magnitude M is the apparent magnitude m the object would have at 10 parsecs,
 * so the two differ by the distance modulus:
 *
 *     m − M = 5 log₁₀(d / 10 pc) = 5 log₁₀ d − 5,   d in parsecs
 *
 * With interstellar extinction of A magnitudes dimming the light on its way, m − M = 5 log₁₀
 * d − 5 + A.
 */

import {
  ASTRONOMICAL_UNIT,
  LIGHT_YEAR,
} from "@/lib/tool-kit/physics-fields/constants";
import type { UnitDef } from "@/lib/tool-kit/physics-fields/units";

const METRES_PER_PARSEC = (648_000 / Math.PI) * ASTRONOMICAL_UNIT;

/** Distance units as a size in parsecs. */
export const MAGNITUDE_DISTANCE_UNITS: readonly UnitDef[] = [
  { id: "pc", symbol: "pc", factor: 1 },
  { id: "ly", symbol: "ly", factor: LIGHT_YEAR / METRES_PER_PARSEC },
  { id: "kpc", symbol: "kpc", factor: 1e3 },
  { id: "mpc", symbol: "Mpc", factor: 1e6 },
  { id: "au", symbol: "AU", factor: ASTRONOMICAL_UNIT / METRES_PER_PARSEC },
];

/** The Sun's nominal bolometric absolute magnitude, IAU 2015 Resolution B2. */
export const SUN_BOLOMETRIC_MAGNITUDE = 4.74;
/** The Sun's absolute V magnitude in the Vega system, Willmer (2018, ApJS 236, 47). */
export const SUN_V_MAGNITUDE = 4.81;

/** m − M for a distance in parsecs, without extinction. */
export function distanceModulus(parsecs: number): number {
  return 5 * Math.log10(parsecs) - 5;
}

export function absoluteMagnitude(
  apparent: number,
  parsecs: number,
  extinction = 0
): number {
  return apparent - distanceModulus(parsecs) - extinction;
}

export function apparentMagnitude(
  absolute: number,
  parsecs: number,
  extinction = 0
): number {
  return absolute + distanceModulus(parsecs) + extinction;
}

/** Distance in parsecs from the two magnitudes (and extinction). */
export function distanceFromMagnitudes(
  apparent: number,
  absolute: number,
  extinction = 0
): number {
  return 10 ** ((apparent - absolute - extinction + 5) / 5);
}

/** How many times brighter an object of magnitude `a` is than one of magnitude `b`. */
export function brightnessRatio(a: number, b: number): number {
  return 10 ** (0.4 * (b - a));
}

/** Luminosity in solar units from an absolute magnitude and the Sun's in the same band. */
export function solarLuminosity(absolute: number, sunAbsolute: number): number {
  return brightnessRatio(absolute, sunAbsolute);
}

export type MagnitudeMode = "absolute" | "apparent" | "distance";

export type MagnitudeSolution =
  | { ok: true; apparent: number; absolute: number; parsecs: number }
  | { ok: false; error: "missing" | "distance" | "extinction" };

/**
 * Solve m − M = 5 log₁₀ d − 5 + A for the unknown named by `mode`. Boxes that are not
 * needed for that mode may be null.
 */
export function solveMagnitudes(
  mode: MagnitudeMode,
  input: {
    apparent: number | null;
    absolute: number | null;
    parsecs: number | null;
    extinction: number | null;
  }
): MagnitudeSolution {
  const { apparent, absolute, parsecs, extinction } = input;
  if (extinction === null) {
    return { ok: false, error: "extinction" };
  }
  if (mode === "distance") {
    if (apparent === null || absolute === null) {
      return { ok: false, error: "missing" };
    }
    const d = distanceFromMagnitudes(apparent, absolute, extinction);
    return Number.isFinite(d) && d > 0
      ? { ok: true, apparent, absolute, parsecs: d }
      : { ok: false, error: "missing" };
  }
  if (parsecs === null) {
    return { ok: false, error: "missing" };
  }
  if (parsecs <= 0) {
    return { ok: false, error: "distance" };
  }
  if (mode === "absolute" && apparent !== null) {
    return {
      ok: true,
      apparent,
      absolute: absoluteMagnitude(apparent, parsecs, extinction),
      parsecs,
    };
  }
  if (mode === "apparent" && absolute !== null) {
    return {
      ok: true,
      apparent: apparentMagnitude(absolute, parsecs, extinction),
      absolute,
      parsecs,
    };
  }
  return { ok: false, error: "missing" };
}
